import { acceptHMRUpdate, defineStore } from 'pinia';
import { isDefined } from '~/utils/guards/isDefined';
import type { LocationQuery } from 'vue-router';
import type { LocationQueryValue } from '#vue-router';
import type {
  ProductsResponseModel,
  ProductsEntryModel
} from '~/lib/ProductsService/model';
import type { ProductsQueryModel } from '~/server/api/products/index.get';
import { buildUrlString } from '~/utils/buildUrlString';
import type { HyperlinkInterface } from '@hypercodestudio/basler-components/dist/components/helpers/HyperLink.vue';
import { useLogger } from '~/composables/useLogger';

const contentfulUrlCache = new Map<string, HyperlinkInterface>();

// The name of the filter which must be set to enable selection of other filters
const PRODUCT_CATEGORY_FILTER_NAME = 'categories.level0';

const ALLOWED_FACET_KEYS = [
  'categories.level0',
  'categories.level1',
  'categories.level2',
  'price'
];

const CATEGORY_ANALYTICS_MAP: Record<string, string> = {
  [PRODUCT_CATEGORY_FILTER_NAME]: 'product-category_filter_selection',
  'categories.level1': 'product-subcategory_filter_selection',
  'categories.level2': 'product-type_filter_selection',
  price: 'price-range_filter_selection'
};

const PRODUCTS_PER_PAGE = 9;

export const useProductsStore = defineStore('products', () => {
  const query = ref<string>('');
  const staticFilters = ref<Record<string, string[]>>({});
  const filters = ref<Record<string, string[]>>({});
  const loading = ref<boolean | undefined>(undefined);
  const product = ref<ProductsEntryModel | undefined>(undefined);
  const products = ref<ProductsResponseModel | null>(null);
  const offset = ref<number>(0);
  const indexName = ref<string>('');
  const queryId = ref<string>('');
  const selectedSortOption = ref<string>('orderedQty');

  const _state = {
    query,
    staticFilters,
    filters,
    loading,
    product,
    products,
    offset,
    indexName,
    queryId,
    selectedSortOption
  };

  const mergedFilters = computed(() => {
    return {
      ...filters.value,
      ...staticFilters.value
    };
  });

  const hasActiveFilters = computed<boolean>(() =>
    Object.values(filters.value).some((values) => !isEmpty(values))
  );

  const haveRequiredFiltersValues = computed<boolean>(() => {
    return (
      !isEmpty(filters.value[PRODUCT_CATEGORY_FILTER_NAME]) ||
      // or any other filter (might be set via url params or manually after
      // selecting the required parameter
      hasActiveFilters.value
    );
  });

  /**
   * A map which contains a filter name (key) and a enabled state (value)
   * for that filter.
   * Filters are only enabled if the necessary filter has a value or
   * any other filter has a value (e.g. via url parameters or manually
   * set after selecting the necessary filter)
   * "Product Category" filter must be enabled.
   */
  const filterDisabledList = computed<Record<string, boolean>>(() => {
    return Object.fromEntries(
      products.value?.filters.map((facet) => [
        facet.name,
        facet.name !== PRODUCT_CATEGORY_FILTER_NAME &&
          !haveRequiredFiltersValues
      ]) ?? []
    );
  });

  /**
   * Flag if the initial view is present or not.
   */
  const isInitialView = computed<boolean>(() => {
    return (
      !hasActiveFilters.value &&
      products.value != null &&
      products.value.results.length !== 0 &&
      products.value.query?.length === 0
    );
  });

  const canLoadMore = computed<boolean>(() => {
    return (
      (products.value &&
        products.value?.filters.length !== 0 &&
        products.value?.results.length < products.value?.maxCount) ??
      false
    );
  });

  async function updateFromRouteAndFetch(
    routeQuery: LocationQuery
  ): Promise<void> {
    const routeFilters: Record<string, string[]> = {};

    for (const key in routeQuery) {
      // if (!ALLOWED_FACET_KEYS.includes(key)) {
      //   continue;
      // }
      if (['page', 'q', 'type', 'sort'].includes(key)) {
        continue;
      }
      const value = routeQuery[key];
      routeFilters[key] = ensureArray(value)
        .map((value) => value?.toString())
        .filter(isDefined);
    }

    filters.value = routeFilters;

    // Set sort option from route parameter if it exists
    if (routeQuery.sort) {
      selectedSortOption.value = routeQuery.sort.toString();
    }

    // Set offset based on page parameter if it exists
    if (routeQuery.page) {
      const page = parseInt(routeQuery.page.toString(), 10);
      if (!isNaN(page) && page > 0) {
        offset.value = (page - 1) * PRODUCTS_PER_PAGE;
      } else {
        offset.value = 0;
      }
    } else {
      // Reset offset to 0 when no page parameter is present
      offset.value = 0;
    }

    await fetchProducts();
  }

  async function decorateProducts(requestData: ProductsResponseModel) {
    for (const product1 of requestData.results) {
      const pageId = findContentfulPageId(product1);
      if (pageId) {
        let data = undefined;
        if (contentfulUrlCache.has(pageId)) {
          data = contentfulUrlCache.get(pageId);
          product1.seriesLink = data;
          continue;
        }

        data = await createProductSeriesLink(pageId);
        if (data) {
          product1.seriesLink = product1.seriesLink = {
            uri: data,
            external: false,
            target: ''
          } as HyperlinkInterface;
          contentfulUrlCache.set(pageId, product1.seriesLink);
        }
      }
    }
    return requestData;
  }

  function findContentfulPageId(
    product: ProductsEntryModel
  ): string | undefined {
    const categories = product.categories_with_contentful_id;
    if (!categories) {
      return;
    }
    // Check level2 first
    const level2Entry = categories.level2?.find((entry) => {
      return entry.contentful_page_id;
    });
    if (level2Entry) {
      return level2Entry.contentful_page_id;
    }

    // Then check level1
    const level1Entry = categories.level1?.find(
      (entry) => entry.contentful_page_id
    );
    if (level1Entry) {
      return level1Entry.contentful_page_id;
    }

    // Finally check level0
    const level0Entry = categories.level0?.find(
      (entry) => entry.contentful_page_id
    );
    if (level0Entry) {
      return level0Entry.contentful_page_id;
    }

    // Return undefined if no contentful_page_id is found
    return undefined;
  }

  async function getContentFulPageById(id: string) {
    const locale = 'de-de';
    const logger = useLogger();

    try {
      const response = await $fetch(
        `/api/contentful/slugById/${id}?locale=${locale}`
      );
      return buildUrlString(locale, response.slug);
    } catch (error) {
      // Log the error but don't throw it further
      logger.error(`Could not fetch contentful page by id "${id}"`, error);
      return undefined;
    }
  }

  const createProductSeriesLink = async (pageId: string) => {
    if (contentfulUrlCache.has(pageId)) {
      return contentfulUrlCache.get(pageId);
    }

    // get highest entry of categories_with_contentful_id that has contentful_id

    if (pageId) {
      const url = await getContentFulPageById(pageId);
      if (url) {
        const link = {
          uri: url,
          external: false,
          target: ''
        } as HyperlinkInterface;
        contentfulUrlCache.set(pageId, link);
        return url;
      }
    }

    return undefined;
  };

  async function fetchProducts(): Promise<void> {
    const { $resolvedLocale } = useNuxtApp();
    loading.value = true;

    // XXX: abort controller
    // XXX: error handling
    const requestData = await $fetch<ProductsResponseModel>('/api/products', {
      method: 'get',
      query: {
        query: query.value,
        locale: $resolvedLocale.value!,
        filters: JSON.stringify(mergedFilters.value),
        limit: PRODUCTS_PER_PAGE.toString(),
        offset: offset.value.toString(),
        ...(selectedSortOption.value && { sort: selectedSortOption.value })
      } satisfies ProductsQueryModel
    });
    // products.value = requestData
    products.value = await decorateProducts(requestData);
    loading.value = false;
    indexName.value = requestData.index;
    queryId.value = requestData.queryId;
  }

  async function fetchMoreProducts(): Promise<void> {
    const { $resolvedLocale } = useNuxtApp();

    offset.value = products.value?.results?.length ?? 0;

    // XXX: abort controller
    // XXX: error handling
    const moreProducts = await $fetch<ProductsResponseModel>('/api/products', {
      method: 'get',
      query: {
        query: query.value,
        locale: $resolvedLocale.value!,
        filters: JSON.stringify(mergedFilters.value),
        limit: PRODUCTS_PER_PAGE.toString(),
        offset: offset.value.toString()
      } satisfies ProductsQueryModel
    });
    if (products.value && products.value.results) {
      products.value.results = products.value.results.concat(
        moreProducts.results
      );
    }
  }

  async function fetchProductById(id: string): Promise<void> {
    const { $resolvedLocale } = useNuxtApp();
    const logger = useLogger();

    try {
      product.value = await $fetch<ProductsEntryModel>('/api/products/byId', {
        method: 'get',
        query: {
          id,
          locale: $resolvedLocale.value
        }
      });
    } catch (e) {
      logger.error(`could not load product by id "${id}"`, e);
    }
  }

  function handleFormValueChange(key: string, value: string | string[]) {
    const { $analytics } = useNuxtApp();

    filters.value = {
      ...filters.value,
      [key]: ensureArray(value)
    };

    if (CATEGORY_ANALYTICS_MAP[key]) {
      $analytics?.pushToDataLayer({
        action: CATEGORY_ANALYTICS_MAP[key],
        category: 'products_filter_selection',
        event: 'click_Internal',
        label: value
      });
    }

    // Reset offset to 0 when filters change
    offset.value = 0;

    updateRoute(filters.value);
  }

  function updateRoute(filters: Record<string, string[]>) {
    const router = useRouter();

    // When filters change, we should reset to page 1
    const query = { ...createUrlParams(filters).query };

    // Remove the page parameter to reset to page 1
    if (query.page) {
      delete query.page;
    }

    // Include sort parameter if it exists
    if (selectedSortOption.value) {
      query.sort = selectedSortOption.value;
    }

    router.push({ query });
  }

  function handleSortChange(sortValue: string) {
    selectedSortOption.value = sortValue;

    // Reset to page 1 when sort changes
    offset.value = 0;

    // Update URL with new sort parameter
    const router = useRouter();
    const route = useRoute();
    const query = { ...route.query, sort: sortValue };

    // Remove page parameter to reset to page 1
    if ('page' in query) {
      delete query.page;
    }

    router.push({ query });

    // Note: Don't call fetchProducts() here - the route change will trigger
    // the search automatically via the SearchPage route watcher
  }

  function createUrlParams(newFilters?: Record<string, string[]>) {
    const route = useRoute();
    const clonedFilters = _cloneFilters(newFilters ?? filters.value);
    const reducer: Record<string, LocationQueryValue | LocationQueryValue[]> =
      {};
    const filteredQueries = Object.keys(route.query)
      .filter((key: string) => !ALLOWED_FACET_KEYS.includes(key))
      .reduce((obj, key) => {
        obj[key] = route.query[key];
        return obj;
      }, reducer);
    return {
      query: { ...filteredQueries, ...clonedFilters }
    };
  }

  function _cloneFilters(
    newFilters?: Record<string, string[]>
  ): Record<string, string[]> {
    return JSON.parse(JSON.stringify(newFilters ?? filters.value));
  }

  function resetFilters() {
    filters.value = {};
    // Reset offset to 0 when filters are reset
    offset.value = 0;
    updateRoute(filters.value);
  }

  function createNewUrlParamsFromFilter(key: string, valueToRemove: string) {
    const clonedFilters = _cloneFilters();
    const values = filters.value[key];
    const arrayValues = ensureArray(values).filter(isDefined).map(String);

    clonedFilters[key] = arrayValues.filter(
      (value) => value && !valueToRemove.includes(value)
    );

    return createUrlParams(clonedFilters);
  }

  /**
   * Calculate total pages based on maxCount and PRODUCTS_PER_PAGE
   */
  const totalPages = computed<number>(() => {
    if (!products.value?.maxCount) {
      return 1;
    }
    return Math.ceil(products.value.maxCount / PRODUCTS_PER_PAGE);
  });

  /**
   * Fetch products for a specific page
   */
  async function fetchProductsByPage(page: number): Promise<void> {
    if (page < 1) {
      page = 1;
    }

    // Calculate offset based on page number
    offset.value = (page - 1) * PRODUCTS_PER_PAGE;

    loading.value = true;

    try {
      // Make the API request with the calculated offset
      const requestData = await $fetch<ProductsResponseModel>('/api/products', {
        method: 'get',
        query: {
          query: query.value,
          // locale: $resolvedLocale.value!,
          filters: JSON.stringify(mergedFilters.value),
          limit: PRODUCTS_PER_PAGE.toString(),
          offset: offset.value.toString(),
          ...(selectedSortOption.value && { sort: selectedSortOption.value })
        } satisfies ProductsQueryModel
      });

      // Update the products in the store with the new results
      products.value = await decorateProducts(requestData);
    } catch (error) {
      console.error('Error fetching products for page:', error);
    } finally {
      loading.value = false;
    }
  }

  return {
    _state,
    query,
    queryId,
    indexName,
    staticFilters,
    filters,
    loading,
    product,
    products,
    offset,
    selectedSortOption,
    hasActiveFilters,
    haveRequiredFiltersValues,
    filterDisabledList,
    isInitialView,
    canLoadMore,
    totalPages,
    updateFromRouteAndFetch,
    fetchProducts,
    fetchProductsByPage,
    fetchMoreProducts,
    fetchProductById,
    handleFormValueChange,
    handleSortChange,
    resetFilters,
    createNewUrlParamsFromFilter
  };
});

// Helper function to ensure we always have an array
function ensureArray<T>(value: T | T[] | undefined | null): T[] {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

// Helper function to check if an array is empty
function isEmpty(arr: unknown[] | undefined | null): boolean {
  return !arr || arr.length === 0;
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProductsStore, import.meta.hot));
}
